import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Wrench, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Check interval in milliseconds (30 seconds)
const CHECK_INTERVAL = 30000;

const MaintenanceBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const [maintenanceActive, setMaintenanceActive] = useState(false);
  const [maintenanceData, setMaintenanceData] = useState({
    message: null,
    end_time: null
  });

  const isAdmin = user?.role === 'admin' || user?.role === 'sub_admin';

  const checkMaintenanceStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/site-settings/public/maintenance`);
      const data = response.data;
      
      setMaintenanceActive(data.maintenance_mode);
      setMaintenanceData({
        message: data.maintenance_message,
        end_time: data.maintenance_end_time
      });
      
      // If maintenance mode just got enabled and user is not admin, redirect
      if (data.maintenance_mode && user && !isAdmin) {
        // Force logout and redirect to maintenance page
        logout();
        navigate('/maintenance');
      }
      
      // If maintenance mode is disabled and we're on the maintenance page, redirect to home
      if (!data.maintenance_mode && location.pathname === '/maintenance') {
        navigate('/');
      }
      
    } catch (error) {
      // If we get a 503 and we're logged in as non-admin, redirect
      if (error.response?.status === 503 && user && !isAdmin) {
        logout();
        navigate('/maintenance');
      }
    }
  }, [user, isAdmin, logout, navigate, location.pathname]);

  useEffect(() => {
    // Initial check
    checkMaintenanceStatus();
    
    // Set up periodic check
    const interval = setInterval(checkMaintenanceStatus, CHECK_INTERVAL);
    
    return () => clearInterval(interval);
  }, [checkMaintenanceStatus]);

  // Set up axios interceptor to catch 503 responses
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check if it's a maintenance mode 503
        if (error.response?.status === 503) {
          const detail = error.response.data?.detail;
          if (detail?.maintenance_mode) {
            // Update maintenance state
            setMaintenanceActive(true);
            setMaintenanceData({
              message: detail.message,
              end_time: detail.end_time
            });
            
            // If not admin, force logout and redirect
            if (!isAdmin) {
              logout();
              navigate('/maintenance');
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [isAdmin, logout, navigate]);

  // Add body padding when banner is visible
  useEffect(() => {
    const shouldShowBanner = maintenanceActive && user && isAdmin && location.pathname !== '/maintenance';
    
    if (shouldShowBanner) {
      document.body.style.paddingTop = '40px';
    } else {
      document.body.style.paddingTop = '0px';
    }
    
    return () => {
      document.body.style.paddingTop = '0px';
    };
  }, [maintenanceActive, user, isAdmin, location.pathname]);

  // Don't show banner if:
  // - Maintenance is not active
  // - User is not logged in
  // - User is not an admin
  // - We're on the maintenance page
  if (!maintenanceActive || !user || !isAdmin || location.pathname === '/maintenance') {
    return null;
  }

  const formatEndTime = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return null;
    }
  };

  const formattedEndTime = formatEndTime(maintenanceData.end_time);

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg"
      data-testid="maintenance-banner"
    >
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-center gap-3 text-sm">
          <Wrench className="w-4 h-4 flex-shrink-0 animate-pulse" />
          <span className="font-medium">
            Maintenance Mode Active
          </span>
          <span className="hidden sm:inline text-red-200">|</span>
          <span className="hidden sm:inline text-red-100">
            {maintenanceData.message || 'Only admins can access the site'}
          </span>
          {formattedEndTime && (
            <>
              <span className="hidden md:inline text-red-200">|</span>
              <span className="hidden md:flex items-center gap-1 text-red-100">
                <Clock className="w-3 h-3" />
                Until: {formattedEndTime}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceBanner;
